/**
 * MOCK API (no backend yet — infra/services don't exist). Mirrors the intended endpoints so the
 * real client is a drop-in later:
 *
 *  - uploadAttachment(file)  → presigned S3 upload (NFR-10). Returns the stored object KEY; the
 *    submit body carries keys, not bytes (see @td/shared submitReportInputSchema.attachments).
 *  - submitReport(input)     → POST /public/report. Returns a high-entropy tracking code
 *    (ADR-0005) the resident uses to check status. Anonymous ⇒ no notification.
 *
 * All shapes align with @td/shared so swapping in `fetch` calls is mechanical.
 */
import { submitReportInputSchema, type SubmitReportInput } from "@td/shared";

export interface SubmitReportResult {
  trackingCode: string;
  /** Lifecycle status the report starts in (parity with the live API). */
  status: string;
  /** Full tracking URL the confirmation surface offers to copy. */
  trackingUrl: string;
  requestId: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly retriable = true,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Mocked presigned upload. In prod: POST /public/uploads → PUT to S3 → key. */
export async function uploadAttachment(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  // Simulate progress + latency so the tile progress bar (intake/spec §4) is exercised.
  for (const pct of [15, 45, 80, 100]) {
    await delay(90);
    onProgress?.(pct);
  }
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-80);
  return `tenants/linnvalley/uploads/${cryptoRandomId()}-${safeName}`;
}

/**
 * Mocked reverse geocode. In prod: GET /public/reverse-geocode?lat=&lng= → Amazon Location
 * (`td-places` Place Index) top result label. Returns a canned Linn Valley address so
 * dev-with-mocks exercises the auto-fill flow offline.
 */
export async function reverseGeocode(_lat: number, _lng: number): Promise<string | null> {
  await delay(400);
  return "412 Lakeshore Dr, Linn Valley, KS";
}

/** Mocked report submission. Validates with the SHARED schema first (same rule as the server). */
export async function submitReport(input: SubmitReportInput): Promise<SubmitReportResult> {
  // Client + server validate against the identical zod schema (NFR-11 / ADR-0010).
  const parsed = submitReportInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new ApiError("The report failed validation. Please review the form.", false);
  }
  await delay(650);
  const trackingCode = makeTrackingCode();
  const requestId = cryptoRandomId();
  const origin = typeof window !== "undefined" ? window.location.origin : "https://towndesk.us";
  return {
    trackingCode,
    status: "New",
    trackingUrl: `${origin}/track/${trackingCode}`,
    requestId,
  };
}

/** Display-formatted tracking code, e.g. LV-7Q4K-9F2X (spec confirmation §2). */
function makeTrackingCode(): string {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // no ambiguous 0/O/1/I/L
  const block = (n: number) =>
    Array.from({ length: n }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join("");
  return `LV-${block(4)}-${block(4)}`;
}

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
